import { useContext, useMemo } from 'react';

import { AreaCalculationType, MATERIAL_OPTIONS } from '@common/constants/MaterialOptions';
import { SideAreaAndLabor, getAreaAndLaborQuantity } from '@common/utils';
import { calculateBaseShapeArea } from '@common/utils/areaUtils';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { DrawingShapeContext } from '@ee-context';

/**
 * @type UseShapeAreaReturn extends SideAreaAndLabor
 * @property {AreaCalculationType} selectedMaterialAreaType What type of calculation to use for the selected material area - raw or manufactured
 *  This is necessary for calls to calculateShapeArea
 *
 * @property {number} totalBaseCountertopArea This is the sum of the base areas for all the countertops, calculated for raw or manufactured values,
 *  depending on the selected material settings. Does not include backsplash or waterfall areas
 *
 * @property {number} totalMaterialArea the sum of the base areas for all the countertops, backsplash and waterfall areas
 */
type UseShapeAreaReturn = SideAreaAndLabor & {
    selectedMaterialAreaType: AreaCalculationType;
    totalBaseCountertopArea: number;
    totalMaterialArea: number;
};

export const useShapeArea = (): UseShapeAreaReturn => {
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { drawingShapes } = useContext(DrawingShapeContext);

    const selectedMaterialAreaType = useMemo(() => {
        const materialOption = MATERIAL_OPTIONS.find((option) =>
            selectedMaterial?.description?.match(option.nameMatcher)
        );
        return materialOption?.areaType ?? AreaCalculationType.Manufactured;
    }, [selectedMaterial?.description]);

    /**
     * this is the sum of the base areas for all the countertops, calculated for raw or manufactured values,
     * depending on the selected material settings
     */
    const totalBaseCountertopArea =
        drawingShapes?.reduce((acc, next) => {
            return acc + calculateBaseShapeArea(next, selectedMaterialAreaType);
        }, 0) ?? 0;

    const {
        totalBacksplashArea,
        totalWaterfallArea,
        totalWaterfallLaborEdgeLengths,
        totalEndCapLaborEdgeCount,
        totalEndSplashLaborEdgeCount,
    } = getAreaAndLaborQuantity(drawingShapes ?? []);

    const totalMaterialArea = totalBaseCountertopArea + totalBacksplashArea + totalWaterfallArea;

    return {
        selectedMaterialAreaType,
        totalBaseCountertopArea,
        totalMaterialArea,
        totalWaterfallArea,
        totalWaterfallLaborEdgeLengths,
        totalEndCapLaborEdgeCount,
        totalEndSplashLaborEdgeCount,
        totalBacksplashArea,
    };
};
